'use client'

import type { CityAreaMapCameraPadding } from '@/lib/mock/cities'
import {
  getCities,
  getGermanyOverviewLocations,
  type CityId,
  type MapView,
  type MapViewId,
} from '@/lib/mock/mapLocations'
import maplibregl from 'maplibre-gl'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import {
  hasContainerSizeChanged,
  LocationMapNavigationHandlers,
  shouldSkipCameraFit,
  syncContainerSizeCache,
  useLocationMapContainerResizeObserver,
} from '@/components/Location/LocationMapNavigation'
import { MapContentChrome } from '@/components/Location/MapContentChrome'
import {
  LocationAreaMapUniversityRoute,
  type LocationAreaMapUniversityRouteData,
} from '@/components/Location/LocationAreaMapUniversityRoute'
import { MapPinHoverCard } from '@/components/Location/MapPinHoverCard'
import type { MapCityCardData } from '@/lib/cities/mapCityData'
import { getRouteMidpoint, getRouteZoomBetweenPoints } from '@/lib/geo'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import { Map as MapCanvas, useMap } from '@/components/ui/map'
import { cn } from '@/utilities/ui'

const BRAND_CREAM = '#FBFAF1'
const AREA_MAP_LAND = BRAND_CREAM
const AREA_MAP_WATER = '#F0EFE6'
const AREA_MAP_ROADS = '#E3E1D7'
const AREA_MAP_BORDERS = '#2b271f'
const AREA_MAP_LABEL = '#9C9890'
export const MAP_TRANSITION_MS = 900

export type AreaMapColorPalette = {
  borders: string
  bordersOpacity?: number
  countryBorders?: string
  countryBordersOpacity?: number
  hideBuildings?: boolean
  hidePois?: boolean
  label: string
  labelCity?: string
  labelDistrict?: string
  land: string
  forest?: string
  grass?: string
  landuseResidential?: string
  park?: string
  roads: string
  roadsMajor?: string
  roadsSecondary?: string
  roadsWidthScale?: number
  water: string
}

export const DEFAULT_AREA_MAP_PALETTE: AreaMapColorPalette = {
  land: AREA_MAP_LAND,
  water: AREA_MAP_WATER,
  roads: AREA_MAP_ROADS,
  borders: AREA_MAP_BORDERS,
  label: AREA_MAP_LABEL,
}

/** @deprecated Use CITY_MARKETING_MAP_PALETTE for homepage and city hero maps. */
export const HERO_AREA_MAP_PALETTE: AreaMapColorPalette = {
  land: AREA_MAP_LAND,
  water: '#E6E4DA',
  roads: '#B8B4A8',
  borders: AREA_MAP_BORDERS,
  label: '#8A8680',
}

/** Cream basemap with readable road hierarchy for homepage and city pages. */
export const CITY_MARKETING_MAP_PALETTE: AreaMapColorPalette = {
  land: AREA_MAP_LAND,
  water: '#ECE9DF',
  park: '#EEEAE1',
  forest: '#E6E2D8',
  grass: '#F0EDE6',
  landuseResidential: '#F8F7F2',
  roads: '#B4B0A8',
  roadsMajor: '#9C9890',
  roadsSecondary: '#A8A49C',
  roadsWidthScale: 0.2,
  borders: '#D0CCC4',
  bordersOpacity: 0.3,
  countryBorders: '#63605A',
  countryBordersOpacity: 0.92,
  label: '#9C9890',
  labelCity: '#6B6760',
  labelDistrict: '#8A8680',
  hideBuildings: true,
  hidePois: true,
}

const ROAD_LAYER_PATTERN = /^(road_|tunnel_.*_(case|fill)|bridge_.*_(case|fill)|aeroway-)/
const MAJOR_ROAD_LAYER_PATTERN = /_(mot|trunk|pri)_/
const SECONDARY_ROAD_LAYER_PATTERN = /_sec_/
const RAIL_LAYER_PATTERN = /^(rail|tunnel_rail)/
const BOUNDARY_LAYER_PATTERN = /^boundary_/
const COUNTRY_BOUNDARY_LAYER_PATTERN = /^boundary_country/
const STATE_BOUNDARY_LAYER_PATTERN = /^boundary_state/
const COUNTY_BOUNDARY_LAYER_PATTERN = /^boundary_county/
const WATER_LAYER_PATTERN = /^(water|waterway)/
const COUNTRY_LABEL_LAYER_PATTERN = /^place_country_/
const CITY_LABEL_LAYER_PATTERN = /^place_city_/
const DISTRICT_LABEL_LAYER_PATTERN = /^place_(suburbs|town|villages|hamlet|state)/
export const NEIGHBOURHOOD_LABEL_LAYER_IDS = ['place_suburbs', 'place_hamlet'] as const
const POI_LAYER_PATTERN = /^poi_/
const HIDDEN_SYMBOL_LAYER_PATTERN = /^(housenumber|watername_)/
const PARK_FILL_LAYER_PATTERN = /^park_/
const LANDCOVER_FILL_LAYER_ID = 'landcover'
const LANDUSE_FILL_LAYER_PATTERN = /^landuse/
const ROADNAME_LAYER_PATTERN = /^roadname_/
const MOBILE_AREA_MAP_MEDIA_QUERY = '(max-width: 767px)'
const MOBILE_ROAD_WIDTH_SCALE = 0.2
const MOBILE_OVERVIEW_MAJOR_ROAD_MIN_ZOOM = 1

/** Show secondary roads and district labels at city zoom levels used on marketing maps. */
export const CITY_MAP_LAYER_MINZOOM: Record<string, number> = {
  bridge_minor_case: 10,
  bridge_minor_fill: 10,
  bridge_sec_case: 10,
  bridge_sec_fill: 10,
  place_hamlet: 9,
  place_suburbs: 9,
  road_minor_case: 10,
  road_minor_fill: 10,
  road_path: 11,
  road_sec_case_noramp: 10,
  road_sec_fill_noramp: 10,
  road_service_case: 11,
  road_service_fill: 11,
  roadname_major: 10,
  roadname_minor: 12,
  roadname_pri: 11,
  roadname_sec: 11,
  tunnel_minor_case: 10,
  tunnel_minor_fill: 10,
  tunnel_sec_case: 10,
  tunnel_sec_fill: 10,
}

export const MARKER_PILL_CLASS =
  'locations-map-marker inline-flex max-w-[15rem] items-center gap-1 rounded-full border border-black/10 px-2.5 py-1.5 font-outfit text-base max-[575px]:text-sm font-medium shadow-[0px_6px_20px_0px_rgba(0,33,64,0.12)]'

/** Matches `max-w-[15rem]` on marker pills — used for camera padding buffers. */
export const MARKER_PILL_MAX_WIDTH_PX = 240

const MARKER_PILL_LABEL_BUFFER_HORIZONTAL_PX = Math.ceil(MARKER_PILL_MAX_WIDTH_PX / 2) + 20
const MARKER_PILL_LABEL_BUFFER_TOP_PX = 56
const MARKER_PILL_LABEL_BUFFER_BOTTOM_PX = 40

export type MapBoundsPadding = {
  bottom: number
  left: number
  right: number
  top: number
}

/** Extra camera padding so pill labels (anchored at bottom) stay inside the map viewport. */
export function getMarkerLabelHorizontalBuffer(containerWidth?: number): number {
  if (containerWidth == null || containerWidth >= NARROW_MOBILE_MAP_BREAKPOINT_PX) {
    return MARKER_PILL_LABEL_BUFFER_HORIZONTAL_PX
  }

  return Math.min(
    MARKER_PILL_LABEL_BUFFER_HORIZONTAL_PX,
    Math.max(48, Math.round(containerWidth * 0.2)),
  )
}

export function addMarkerLabelPadding(
  padding: MapBoundsPadding,
  containerWidth?: number,
): MapBoundsPadding {
  const horizontalBuffer = getMarkerLabelHorizontalBuffer(containerWidth)

  return {
    top: padding.top + MARKER_PILL_LABEL_BUFFER_TOP_PX,
    bottom: padding.bottom + MARKER_PILL_LABEL_BUFFER_BOTTOM_PX,
    left: padding.left + horizontalBuffer,
    right: padding.right + horizontalBuffer,
  }
}

/** Route mode fits two pill labels on opposite sides — reserve extra horizontal inset. */
export function addRouteMarkerLabelPadding(
  padding: MapBoundsPadding,
  containerWidth?: number,
): MapBoundsPadding {
  const withLabel = addMarkerLabelPadding(padding, containerWidth)

  return {
    ...withLabel,
    left: withLabel.left + 72,
    right: withLabel.right + 72,
  }
}

/** Whether there is enough horizontal canvas to run bounds fitting (content column vs map). */
export function canFitMapBounds(containerWidth: number, padding: MapBoundsPadding): boolean {
  return padding.left + padding.right <= containerWidth * 0.65
}

/** Scale padding down when the map viewport cannot honor the full structural inset. */
export function clampMapBoundsPadding(
  containerWidth: number,
  containerHeight: number,
  padding: MapBoundsPadding,
): MapBoundsPadding {
  const maxHorizontal = containerWidth * 0.62
  const maxVertical = containerHeight * 0.58
  const horizontalTotal = padding.left + padding.right
  const verticalTotal = padding.top + padding.bottom
  const horizontalScale = horizontalTotal > maxHorizontal ? maxHorizontal / horizontalTotal : 1
  const verticalScale = verticalTotal > maxVertical ? maxVertical / verticalTotal : 1

  return {
    left: Math.round(padding.left * horizontalScale),
    right: Math.round(padding.right * horizontalScale),
    top: Math.round(padding.top * verticalScale),
    bottom: Math.round(padding.bottom * verticalScale),
  }
}

/** Return padding that leaves MapLibre a positive viewport to calculate from. */
export function getSafeMapBoundsPadding(
  containerWidth: number,
  containerHeight: number,
  padding: MapBoundsPadding,
): MapBoundsPadding | undefined {
  const values = [
    containerWidth,
    containerHeight,
    padding.top,
    padding.bottom,
    padding.left,
    padding.right,
  ]

  if (
    values.some((value) => !Number.isFinite(value) || value < 0) ||
    containerWidth <= 2 ||
    containerHeight <= 2
  ) {
    return undefined
  }

  const clamped = clampMapBoundsPadding(containerWidth, containerHeight, padding)
  const maxHorizontal = Math.max(1, Math.floor(containerWidth) - 2)
  const maxVertical = Math.max(1, Math.floor(containerHeight) - 2)
  const horizontalTotal = clamped.left + clamped.right
  const verticalTotal = clamped.top + clamped.bottom
  const horizontalScale = horizontalTotal > maxHorizontal ? maxHorizontal / horizontalTotal : 1
  const verticalScale = verticalTotal > maxVertical ? maxVertical / verticalTotal : 1

  return {
    left: Math.floor(clamped.left * horizontalScale),
    right: Math.floor(clamped.right * horizontalScale),
    top: Math.floor(clamped.top * verticalScale),
    bottom: Math.floor(clamped.bottom * verticalScale),
  }
}

export function getRoadColorForLayer(layerId: string, palette: AreaMapColorPalette): string {
  if (!palette.roadsMajor) {
    return palette.roads
  }

  if (MAJOR_ROAD_LAYER_PATTERN.test(layerId)) {
    return palette.roadsMajor
  }

  if (SECONDARY_ROAD_LAYER_PATTERN.test(layerId)) {
    return palette.roadsSecondary ?? palette.roadsMajor
  }

  return palette.roads
}

export function getLandcoverFillExpression(
  palette: AreaMapColorPalette,
): maplibregl.ExpressionSpecification {
  const forest = palette.forest ?? palette.park ?? palette.land
  const grass = palette.grass ?? palette.park ?? palette.land
  const park = palette.park ?? palette.land

  return ['match', ['get', 'class'], 'wood', forest, 'grass', grass, park]
}

export function getFillColorForLayer(
  layerId: string,
  palette: AreaMapColorPalette,
): string | maplibregl.ExpressionSpecification {
  if (WATER_LAYER_PATTERN.test(layerId)) {
    return palette.water
  }

  if (layerId === LANDCOVER_FILL_LAYER_ID && palette.park) {
    return getLandcoverFillExpression(palette)
  }

  if (PARK_FILL_LAYER_PATTERN.test(layerId) && palette.park) {
    return layerId === 'park_national_park' && palette.forest ? palette.forest : palette.park
  }

  if (layerId === 'landuse' && palette.park) {
    return palette.park
  }

  if (layerId === 'landuse_residential' && palette.landuseResidential) {
    return palette.landuseResidential
  }

  return palette.land
}

export function scaleRoadLineWidth(
  width: unknown,
  scale: number,
): number | maplibregl.ExpressionSpecification {
  if (typeof width === 'number') {
    return width * scale
  }

  if (Array.isArray(width)) {
    const scaled = [...width] as unknown[]
    const valueStartIndex = width[0] === 'interpolate' ? 4 : width[0] === 'step' ? 3 : -1

    if (valueStartIndex > 0) {
      for (let index = valueStartIndex; index < scaled.length; index += 2) {
        const value = scaled[index]
        if (typeof value === 'number') {
          scaled[index] = value * scale
        }
      }

      return scaled as maplibregl.ExpressionSpecification
    }
  }

  if (typeof width === 'object' && width !== null && 'stops' in width) {
    const stops = (width as { stops: [number, number][] }).stops

    return {
      ...(width as object),
      stops: stops.map(([zoom, lineWidth]) => [zoom, lineWidth * scale]),
    } as unknown as maplibregl.ExpressionSpecification
  }

  return width as number
}

const baseRoadLineWidths = new WeakMap<maplibregl.Map, Map<string, unknown>>()

export function getLabelColorForLayer(layerId: string, palette: AreaMapColorPalette): string {
  if (palette.labelCity && CITY_LABEL_LAYER_PATTERN.test(layerId)) {
    return palette.labelCity
  }

  if (
    palette.labelDistrict &&
    NEIGHBOURHOOD_LABEL_LAYER_IDS.includes(
      layerId as (typeof NEIGHBOURHOOD_LABEL_LAYER_IDS)[number],
    )
  ) {
    return palette.labelDistrict
  }

  return palette.label
}

export function getNeighbourhoodLabelFilter(
  layerId: (typeof NEIGHBOURHOOD_LABEL_LAYER_IDS)[number],
): maplibregl.FilterSpecification {
  if (layerId === 'place_hamlet') {
    return [
      'any',
      ['==', 'class', 'neighbourhood'],
      ['==', 'class', 'hamlet'],
      ['==', 'class', 'quarter'],
    ]
  }

  return ['==', 'class', 'suburb']
}

export const NEIGHBOURHOOD_LABEL_TEXT_FIELD: maplibregl.ExpressionSpecification = [
  'coalesce',
  ['get', 'name'],
  ['get', 'name_en'],
]

export const NEIGHBOURHOOD_LABEL_TEXT_SIZE: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  9,
  9,
  10,
  10,
  11,
  10,
  12,
  11,
  14,
  12,
]

export type BoundaryPaint = {
  color: string
  opacity: number
  width: number
}

export function getBoundaryPaintForLayer(
  layerId: string,
  palette: AreaMapColorPalette,
): BoundaryPaint {
  if (COUNTRY_BOUNDARY_LAYER_PATTERN.test(layerId)) {
    return {
      color: palette.countryBorders ?? palette.borders,
      opacity: palette.countryBordersOpacity ?? palette.bordersOpacity ?? 0.75,
      width: layerId.includes('outline') ? 1.3 : 0.75,
    }
  }

  if (COUNTY_BOUNDARY_LAYER_PATTERN.test(layerId)) {
    return {
      color: palette.borders,
      opacity: 0.15,
      width: 0.2,
    }
  }

  if (STATE_BOUNDARY_LAYER_PATTERN.test(layerId)) {
    return {
      color: palette.borders,
      opacity: 0.28,
      width: 0.25,
    }
  }

  return {
    color: palette.borders,
    opacity: palette.bordersOpacity ?? 0.75,
    width: 0.35,
  }
}

export function getCityMapLayerMinZoom(layerId: string): number | undefined {
  return CITY_MAP_LAYER_MINZOOM[layerId]
}

export function applyNeighbourhoodLabelLayers(
  map: maplibregl.Map,
  palette: AreaMapColorPalette = CITY_MARKETING_MAP_PALETTE,
) {
  if (!palette.roadsMajor) {
    return
  }

  const districtColor = palette.labelDistrict ?? palette.label

  for (const layerId of NEIGHBOURHOOD_LABEL_LAYER_IDS) {
    if (!map.getLayer(layerId)) {
      continue
    }

    map.setLayerZoomRange(layerId, 9, 24)
    map.setFilter(layerId, getNeighbourhoodLabelFilter(layerId))
    map.setLayoutProperty(layerId, 'text-field', NEIGHBOURHOOD_LABEL_TEXT_FIELD)
    map.setLayoutProperty(layerId, 'text-transform', 'uppercase')
    map.setLayoutProperty(layerId, 'text-size', NEIGHBOURHOOD_LABEL_TEXT_SIZE)
    map.setLayoutProperty(layerId, 'text-letter-spacing', 0.05)
    map.setPaintProperty(layerId, 'text-color', districtColor)
    map.setPaintProperty(layerId, 'text-halo-color', palette.land)
    map.setPaintProperty(layerId, 'text-halo-width', 1)
  }
}

export function applyCityMapDetailLayers(
  map: maplibregl.Map,
  palette: AreaMapColorPalette = CITY_MARKETING_MAP_PALETTE,
) {
  if (!palette.roadsMajor) {
    return
  }

  for (const [layerId, minZoom] of Object.entries(CITY_MAP_LAYER_MINZOOM)) {
    if (map.getLayer(layerId)) {
      map.setLayerZoomRange(layerId, minZoom, 24)
    }
  }

  applyNeighbourhoodLabelLayers(map, palette)
}

function applyMobileOverviewRoadLayers(map: maplibregl.Map) {
  const layers = map.getStyle()?.layers ?? []

  for (const layer of layers) {
    if (
      layer.type === 'line' &&
      ROAD_LAYER_PATTERN.test(layer.id) &&
      MAJOR_ROAD_LAYER_PATTERN.test(layer.id)
    ) {
      map.setLayerZoomRange(layer.id, MOBILE_OVERVIEW_MAJOR_ROAD_MIN_ZOOM, 24)
    }
  }
}

export function applyAreaMapColors(
  map: maplibregl.Map,
  palette: AreaMapColorPalette = DEFAULT_AREA_MAP_PALETTE,
) {
  const layers = map.getStyle()?.layers ?? []
  const baseWidths = baseRoadLineWidths.get(map) ?? new globalThis.Map<string, unknown>()

  baseRoadLineWidths.set(map, baseWidths)

  for (const layer of layers) {
    const { id, type } = layer

    if (palette.hidePois && POI_LAYER_PATTERN.test(id)) {
      map.setLayoutProperty(id, 'visibility', 'none')
      continue
    }

    if (palette.hideBuildings && id.startsWith('building')) {
      map.setPaintProperty(id, 'fill-color', palette.land)
      map.setPaintProperty(id, 'fill-opacity', 0)
      continue
    }

    if (type === 'background') {
      map.setPaintProperty(id, 'background-color', palette.land)
      continue
    }

    if (type === 'fill') {
      const fillColor = getFillColorForLayer(id, palette)

      map.setPaintProperty(id, 'fill-color', fillColor)

      if (
        palette.park &&
        (id === LANDCOVER_FILL_LAYER_ID ||
          PARK_FILL_LAYER_PATTERN.test(id) ||
          LANDUSE_FILL_LAYER_PATTERN.test(id))
      ) {
        map.setPaintProperty(id, 'fill-opacity', 1)
      }
      continue
    }

    if (type === 'line') {
      if (ROAD_LAYER_PATTERN.test(id)) {
        map.setPaintProperty(id, 'line-color', getRoadColorForLayer(id, palette))
        if (palette.roadsMajor) {
          map.setPaintProperty(id, 'line-opacity', id.includes('_case') ? 0.9 : 1)

          const roadWidthScale = palette.roadsWidthScale
          const lineWidth = (
            layer as maplibregl.LayerSpecification & {
              paint?: { 'line-width'?: unknown }
            }
          ).paint?.['line-width']

          if (roadWidthScale != null && lineWidth !== undefined) {
            if (!baseWidths.has(id)) {
              baseWidths.set(id, lineWidth)
            }

            map.setPaintProperty(
              id,
              'line-width',
              scaleRoadLineWidth(baseWidths.get(id), roadWidthScale),
            )
          }
        }
      } else if (RAIL_LAYER_PATTERN.test(id)) {
        map.setPaintProperty(id, 'line-color', palette.roads)
        map.setPaintProperty(id, 'line-opacity', palette.roadsMajor ? 0.35 : 0.75)
      } else if (WATER_LAYER_PATTERN.test(id)) {
        map.setPaintProperty(id, 'line-color', palette.water)
      } else if (BOUNDARY_LAYER_PATTERN.test(id)) {
        const boundaryPaint = getBoundaryPaintForLayer(id, palette)

        map.setPaintProperty(id, 'line-color', boundaryPaint.color)
        map.setPaintProperty(id, 'line-opacity', boundaryPaint.opacity)
        map.setPaintProperty(id, 'line-width', boundaryPaint.width)
      }
      continue
    }

    if (type === 'symbol') {
      if (palette.hidePois && HIDDEN_SYMBOL_LAYER_PATTERN.test(id)) {
        map.setLayoutProperty(id, 'visibility', 'none')
        continue
      }

      if (!map.getLayoutProperty(id, 'text-field')) {
        continue
      }

      const isCountryLabel = COUNTRY_LABEL_LAYER_PATTERN.test(id)
      const isCityLabel = CITY_LABEL_LAYER_PATTERN.test(id)
      const isDistrictLabel = DISTRICT_LABEL_LAYER_PATTERN.test(id)
      const isRoadNameLabel = ROADNAME_LAYER_PATTERN.test(id)

      map.setPaintProperty(
        id,
        'text-color',
        isRoadNameLabel ? palette.label : getLabelColorForLayer(id, palette),
      )
      map.setPaintProperty(id, 'text-halo-color', palette.land)
      map.setPaintProperty(
        id,
        'text-halo-width',
        isCountryLabel ? 2.5 : isCityLabel ? 1.75 : isRoadNameLabel ? 0.75 : 1,
      )

      if (isRoadNameLabel && palette.roadsMajor) {
        const boostedMinZoom = getCityMapLayerMinZoom(id)
        if (boostedMinZoom != null) {
          map.setLayerZoomRange(id, boostedMinZoom, 24)
        }
      } else if (isCountryLabel) {
        map.setLayoutProperty(
          id,
          'text-size',
          id === 'place_country_1'
            ? ['interpolate', ['linear'], ['zoom'], 3, 15, 4, 16, 5, 17, 6, 18, 7, 19]
            : ['interpolate', ['linear'], ['zoom'], 3, 14, 5, 15, 6, 16, 7, 17, 8, 18],
        )
      } else if (isCityLabel && palette.labelCity) {
        map.setLayoutProperty(id, 'text-size', [
          'interpolate',
          ['linear'],
          ['zoom'],
          8,
          14,
          10,
          16,
          12,
          18,
          14,
          20,
        ])
      } else if (isDistrictLabel && palette.labelCity) {
        map.setLayoutProperty(id, 'text-transform', 'uppercase')
        map.setPaintProperty(id, 'text-color', palette.label)

        const boostedMinZoom = getCityMapLayerMinZoom(id)
        if (boostedMinZoom != null) {
          map.setLayerZoomRange(id, boostedMinZoom, 24)
        }
      }
    }
  }

  applyCityMapDetailLayers(map, palette)
}

type LocationAreaMapProps = {
  className?: string
  fallbackMessage: string
  mapCameraPadding?: CityAreaMapCameraPadding
  mapCities?: MapCityCardData[]
  mapControlLabels: {
    zoomGroupLabel: string
    zoomInLabel: string
    zoomOutLabel: string
  }
  mapLocations?: MapLocationCardData[]
  mapView: MapView
  selectedLocationId: string
  universityRoute?: LocationAreaMapUniversityRouteData
  viewCityLabel: string
}

export function measureLeftInset(
  mapContainer: HTMLElement,
  paddingOptions?: CityAreaMapCameraPadding,
) {
  const width = mapContainer.clientWidth
  const section = mapContainer.closest('section')
  const content = section?.querySelector<HTMLElement>('[data-locations-map-content]')
  const contentGap = paddingOptions?.contentGap ?? 48
  const extraLeft = paddingOptions?.extraLeft ?? 0
  const leftMaxRatio = paddingOptions?.leftMaxRatio ?? 0.72
  const minLeft = paddingOptions?.minLeft ?? 0

  if (!content || width === 0) {
    return Math.max(minLeft, Math.round(width * 0.45) + extraLeft)
  }

  const mapRect = mapContainer.getBoundingClientRect()
  const contentRect = content.getBoundingClientRect()
  const measured = contentRect.right - mapRect.left + contentGap + extraLeft
  const maxLeft = Math.round(width * leftMaxRatio)

  return Math.max(minLeft, Math.min(maxLeft, Math.max(48, Math.round(measured))))
}

export function getBoundsPadding(
  container: HTMLElement,
  paddingOptions?: CityAreaMapCameraPadding,
) {
  const height = container.clientHeight
  const left = paddingOptions?.disableContentInset
    ? (paddingOptions.minLeft ?? 24)
    : measureLeftInset(container, paddingOptions)

  return {
    top:
      paddingOptions?.top ??
      (paddingOptions?.disableContentInset ? 32 : Math.min(96, Math.round(height * 0.12))),
    bottom:
      paddingOptions?.bottom ??
      (paddingOptions?.disableContentInset ? 32 : Math.min(96, Math.round(height * 0.12))),
    left,
    right: paddingOptions?.right ?? 48,
  }
}

export const DESKTOP_MAP_BREAKPOINT_PX = 768
export const NARROW_MOBILE_MAP_BREAKPOINT_PX = 576
export const NARROW_DESKTOP_MAP_BREAKPOINT_PX = 1100
export const COMPACT_DESKTOP_MAP_BREAKPOINT_PX = 1400
/** @deprecated Use NARROW_DESKTOP_MAP_ZOOM_OFFSET */
export const COMPACT_DESKTOP_MAP_ZOOM_OFFSET = -1
export const NARROW_DESKTOP_MAP_ZOOM_OFFSET = -1
export const DESKTOP_MAP_FOCUS_X_RATIO = 0.75

/** Zoom out on narrow desktop widths; mobile uses separate section-level offsets. */
export function getDesktopMapZoomOffset(viewportWidth: number): number {
  if (viewportWidth < DESKTOP_MAP_BREAKPOINT_PX) {
    return 0
  }

  if (viewportWidth < NARROW_DESKTOP_MAP_BREAKPOINT_PX) {
    return NARROW_DESKTOP_MAP_ZOOM_OFFSET
  }

  return 0
}

/** @deprecated Use getDesktopMapZoomOffset */
export function getCompactDesktopMapZoomOffset(viewportWidth: number): number {
  return getDesktopMapZoomOffset(viewportWidth)
}

/**
 * Returns asymmetric horizontal padding whose padded viewport center lands at
 * the requested screen-space ratio. At 0.75, geographic focus sits in the
 * center of the right half of a full-width map.
 */
export function getHorizontalPaddingForFocus(
  containerWidth: number,
  right: number,
  focusXRatio = DESKTOP_MAP_FOCUS_X_RATIO,
): Pick<MapBoundsPadding, 'left' | 'right'> {
  const safeWidth = Math.max(0, containerWidth)
  const safeRight = Math.max(0, right)
  const safeFocusXRatio = Math.min(1, Math.max(0.5, focusXRatio))

  return {
    left: Math.round(safeRight + safeWidth * (safeFocusXRatio * 2 - 1)),
    right: safeRight,
  }
}

/**
 * Keeps mobile camera padding unchanged while targeting the center of the
 * unobstructed right half on desktop.
 */
export function getResponsiveMapFocusPadding(
  container: HTMLElement,
  paddingOptions?: CityAreaMapCameraPadding,
): MapBoundsPadding {
  const padding = getBoundsPadding(container, paddingOptions)

  if (container.clientWidth < DESKTOP_MAP_BREAKPOINT_PX) {
    return padding
  }

  return {
    ...padding,
    ...getHorizontalPaddingForFocus(container.clientWidth, padding.right),
  }
}

export function applyCenterLngOffset(
  center: maplibregl.LngLatLike,
  paddingOptions?: CityAreaMapCameraPadding,
): maplibregl.LngLatLike {
  const lngOffset = paddingOptions?.centerLngOffset ?? 0
  const latOffset = paddingOptions?.centerLatOffset ?? 0

  if (!lngOffset && !latOffset) {
    return center
  }

  if (Array.isArray(center)) {
    return [center[0] + lngOffset, center[1] + latOffset]
  }

  if (typeof center === 'object' && 'lng' in center && 'lat' in center) {
    return { lng: center.lng + lngOffset, lat: center.lat + latOffset }
  }

  return center
}

function isFiniteLngLatLike(value: unknown): boolean {
  if (Array.isArray(value)) {
    return (
      value.length >= 2 &&
      typeof value[0] === 'number' &&
      typeof value[1] === 'number' &&
      Number.isFinite(value[0]) &&
      Number.isFinite(value[1])
    )
  }

  if (value && typeof value === 'object' && 'lng' in value && 'lat' in value) {
    const { lng, lat } = value as { lng: unknown; lat: unknown }
    return (
      typeof lng === 'number' &&
      typeof lat === 'number' &&
      Number.isFinite(lng) &&
      Number.isFinite(lat)
    )
  }

  return false
}

function getSafeCameraForBounds(
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBounds,
  padding: MapBoundsPadding | undefined,
  maxZoom: number,
) {
  if (!padding || !Number.isFinite(maxZoom)) {
    return undefined
  }

  try {
    const camera = map.cameraForBounds(bounds, {
      padding,
      maxZoom,
    })

    return camera && isFiniteLngLatLike(camera.center) ? camera : undefined
  } catch {
    return undefined
  }
}

function getUniversityRouteKey(universityRoute?: LocationAreaMapUniversityRouteData) {
  if (!universityRoute) {
    return ''
  }

  return [
    universityRoute.routeAnimationId,
    universityRoute.routeDirection ?? 'university-to-campus',
    universityRoute.universityCoordinate.join(','),
    universityRoute.campusCoordinate.join(','),
  ].join(':')
}

function fitMapToUniversityRoute(
  map: maplibregl.Map,
  mapView: MapView,
  universityRoute: LocationAreaMapUniversityRouteData,
  container: HTMLElement,
  animated = true,
  paddingOptions?: CityAreaMapCameraPadding,
  onComplete?: () => void,
) {
  const width = container.clientWidth
  const height = container.clientHeight

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 2 || height <= 2) {
    onComplete?.()
    return
  }

  if (hasContainerSizeChanged(container)) {
    map.resize()
    syncContainerSizeCache(container)
  }

  const duration = animated ? MAP_TRANSITION_MS : 0
  const padding = getBoundsPadding(container, paddingOptions)
  const fallbackPadding = getSafeMapBoundsPadding(width, height, padding) ?? {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  }
  const finish = () => {
    onComplete?.()
  }

  map.stop()
  map.flyTo({
    center: applyCenterLngOffset(
      getRouteMidpoint(universityRoute.universityCoordinate, universityRoute.campusCoordinate),
      paddingOptions,
    ),
    duration,
    essential: true,
    padding: fallbackPadding,
    zoom: getRouteZoomBetweenPoints(
      universityRoute.universityCoordinate,
      universityRoute.campusCoordinate,
      mapView.zoom,
    ),
  })

  if (duration === 0) {
    finish()
    return
  }

  map.once('moveend', finish)
}

function fitMapToLocations(
  map: maplibregl.Map,
  mapView: MapView,
  container: HTMLElement,
  animated = true,
  paddingOptions?: CityAreaMapCameraPadding,
  onComplete?: () => void,
) {
  const width = container.clientWidth
  const height = container.clientHeight

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 2 || height <= 2) {
    onComplete?.()
    return
  }

  if (hasContainerSizeChanged(container)) {
    map.resize()
    syncContainerSizeCache(container)
  }

  const duration = animated ? MAP_TRANSITION_MS : 0
  const padding = getBoundsPadding(container, paddingOptions)
  const fallbackPadding = getSafeMapBoundsPadding(width, height, padding) ?? {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  }
  const effectiveZoom = mapView.zoom + getDesktopMapZoomOffset(width)
  const finish = () => {
    onComplete?.()
  }

  const fly = (options: maplibregl.FlyToOptions) => {
    map.stop()
    map.flyTo({
      ...options,
      duration,
      essential: true,
    })

    if (duration === 0) {
      finish()
      return
    }

    map.once('moveend', finish)
  }

  const flyToMapViewCenter = () => {
    fly({
      center: applyCenterLngOffset(mapView.center, paddingOptions),
      zoom: effectiveZoom,
      padding: fallbackPadding,
    })
  }

  if (mapView.locations.length === 0) {
    flyToMapViewCenter()
    return
  }

  // Narrow stacked maps have enough vertical space to use their configured
  // camera directly. Bounds fitting here can overcorrect for marker labels.
  if (width < DESKTOP_MAP_BREAKPOINT_PX) {
    flyToMapViewCenter()
    return
  }

  if (mapView.isOverview) {
    if (paddingOptions?.overviewFitAtZoom) {
      if (!canFitMapBounds(width, padding)) {
        flyToMapViewCenter()
        return
      }

      const bounds = new maplibregl.LngLatBounds()

      mapView.locations.forEach((location) => {
        bounds.extend([location.longitude, location.latitude])
      })

      const overviewZoom = effectiveZoom
      const fitPadding = getSafeMapBoundsPadding(
        width,
        height,
        addMarkerLabelPadding(padding, width),
      )
      const camera = getSafeCameraForBounds(map, bounds, fitPadding, overviewZoom)

      if (camera?.center) {
        fly({
          center: applyCenterLngOffset(camera.center, paddingOptions),
          zoom: overviewZoom,
          padding: fitPadding,
        })
        return
      }
    }

    flyToMapViewCenter()
    return
  }

  const structuralPadding = padding

  // Heavy left inset (landing/city hero layouts) leaves too little canvas for bounds fitting.
  if (!canFitMapBounds(width, structuralPadding)) {
    flyToMapViewCenter()
    return
  }

  const bounds = new maplibregl.LngLatBounds()

  mapView.locations.forEach((location) => {
    bounds.extend([location.longitude, location.latitude])
  })

  if (mapView.locations.length === 1) {
    const [location] = mapView.locations
    const pad = 0.0015
    bounds.extend([location.longitude - pad, location.latitude - pad])
    bounds.extend([location.longitude + pad, location.latitude + pad])
  }

  const maxZoom =
    mapView.locations.length === 1
      ? (mapView.locations[0]?.zoom ?? effectiveZoom + 0.5)
      : effectiveZoom + 0.5

  const fitPadding = getSafeMapBoundsPadding(
    width,
    height,
    addMarkerLabelPadding(structuralPadding, width),
  )
  const camera = getSafeCameraForBounds(map, bounds, fitPadding, maxZoom)

  if (camera?.center) {
    const rawZoom = camera.zoom ?? effectiveZoom
    const isNarrowMobileMultiLocation =
      width < NARROW_MOBILE_MAP_BREAKPOINT_PX && mapView.locations.length > 1
    const resolvedZoom = isNarrowMobileMultiLocation
      ? Math.min(rawZoom, maxZoom)
      : mapView.locations.length === 1
        ? Math.max(rawZoom, effectiveZoom)
        : Math.max(rawZoom, effectiveZoom)

    fly({
      center: applyCenterLngOffset(camera.center, paddingOptions),
      zoom: resolvedZoom,
      bearing: camera.bearing ?? 0,
      padding: fitPadding,
    })
    return
  }

  flyToMapViewCenter()
}

function LocationMapStyle({ isOverview }: { isOverview: boolean }) {
  const { isLoaded, map } = useMap()
  const [isMobileMap, setIsMobileMap] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_AREA_MAP_MEDIA_QUERY).matches,
  )

  useEffect(() => {
    const media = window.matchMedia(MOBILE_AREA_MAP_MEDIA_QUERY)
    const updateMobileMap = () => setIsMobileMap(media.matches)

    updateMobileMap()
    media.addEventListener('change', updateMobileMap)

    return () => {
      media.removeEventListener('change', updateMobileMap)
    }
  }, [])

  useEffect(() => {
    if (!map || !isLoaded) return

    const palette = isMobileMap
      ? { ...CITY_MARKETING_MAP_PALETTE, roadsWidthScale: MOBILE_ROAD_WIDTH_SCALE }
      : CITY_MARKETING_MAP_PALETTE
    const applyMapColors = () => {
      if (!map.isStyleLoaded()) return
      applyAreaMapColors(map, palette)
      if (isMobileMap && isOverview) {
        applyMobileOverviewRoadLayers(map)
      }
    }
    const applyMapColorsAfterStyleLoad = () => {
      baseRoadLineWidths.delete(map)
      applyMapColors()
    }

    applyMapColors()
    map.on('style.load', applyMapColorsAfterStyleLoad)

    return () => {
      map.off('style.load', applyMapColorsAfterStyleLoad)
    }
  }, [isLoaded, isMobileMap, isOverview, map])

  return null
}

function LocationMapCamera({
  mapCameraPadding,
  mapView,
  preserveUserZoom,
  universityRoute,
}: {
  mapCameraPadding?: CityAreaMapCameraPadding
  mapView: MapView
  preserveUserZoom: boolean
  universityRoute?: LocationAreaMapUniversityRouteData
}) {
  const { isLoaded, map } = useMap()
  const isCameraUpdateRef = useRef(false)
  const mapViewRef = useRef(mapView)
  const mapCameraPaddingRef = useRef(mapCameraPadding)
  const universityRouteRef = useRef(universityRoute)
  const preserveUserZoomRef = useRef(preserveUserZoom)
  const previousMapViewIdRef = useRef<MapViewId | null>(null)
  const previousRouteKeyRef = useRef('')
  const onContainerRefitRef = useRef<() => void>(() => {})
  const routeKey = getUniversityRouteKey(universityRoute)

  preserveUserZoomRef.current = preserveUserZoom

  useEffect(() => {
    mapViewRef.current = mapView
    mapCameraPaddingRef.current = mapCameraPadding
    universityRouteRef.current = universityRoute
  }, [mapCameraPadding, mapView, universityRoute])

  onContainerRefitRef.current = () => {
    if (!map) return

    const container = map.getContainer()
    const currentRoute = universityRouteRef.current

    if (currentRoute) {
      fitMapToUniversityRoute(
        map,
        mapViewRef.current,
        currentRoute,
        container,
        false,
        mapCameraPaddingRef.current,
      )
      return
    }

    fitMapToLocations(map, mapViewRef.current, container, false, mapCameraPaddingRef.current)
  }

  useEffect(() => {
    if (!map || !isLoaded) return

    const container = map.getContainer()
    const isViewChange =
      previousMapViewIdRef.current !== null && previousMapViewIdRef.current !== mapView.id
    const isRouteChange = previousRouteKeyRef.current !== routeKey
    const hasPriorCamera =
      previousMapViewIdRef.current !== null || previousRouteKeyRef.current !== ''
    const isFirstLoad = !hasPriorCamera

    if (
      shouldSkipCameraFit({
        isFirstLoad,
        isRouteChange,
        isViewChange,
        preserveUserZoom: preserveUserZoomRef.current,
      })
    ) {
      return
    }

    const animated = hasPriorCamera && (isViewChange || isRouteChange)

    previousMapViewIdRef.current = mapView.id
    previousRouteKeyRef.current = routeKey
    isCameraUpdateRef.current = true

    const currentRoute = universityRouteRef.current
    const finish = () => {
      isCameraUpdateRef.current = false
    }

    if (currentRoute) {
      fitMapToUniversityRoute(
        map,
        mapView,
        currentRoute,
        container,
        animated,
        mapCameraPadding,
        finish,
      )
      return
    }

    fitMapToLocations(map, mapView, container, animated, mapCameraPadding, finish)
  }, [isLoaded, map, mapCameraPadding, mapView, mapView.id, mapView.zoom, routeKey])

  useLocationMapContainerResizeObserver({
    isCameraUpdateRef,
    isLoaded,
    map,
    onRefitRef: onContainerRefitRef,
    preserveUserZoom,
  })

  return null
}

const ALL_OVERVIEW_LOCATIONS = getGermanyOverviewLocations()
const ALL_DETAIL_LOCATIONS = getCities().flatMap((city) => city.locations)

/** When a location is selected, only that city pin is shown; otherwise all city pins render. */
export function shouldShowDetailLocationPin(
  locationId: string,
  selectedLocationId: string,
): boolean {
  if (!selectedLocationId) {
    return true
  }

  return locationId === selectedLocationId
}

function LocationMapErrorWatcher({ onError }: { onError: () => void }) {
  const { map } = useMap()

  useEffect(() => {
    if (!map) return

    const handleError = () => onError()

    map.on('error', handleError)

    return () => {
      map.off('error', handleError)
    }
  }, [map, onError])

  return null
}

function getOverviewMarkerName(mapView: MapView, location: MapView['locations'][number]) {
  if (!mapView.isOverview) {
    return location.name
  }

  return mapView.locations.find((entry) => entry.id === location.id)?.name ?? location.name
}

function getOverviewCityId(locationId: string): CityId | null {
  if (!locationId.startsWith('overview-')) {
    return null
  }

  const cityId = locationId.slice('overview-'.length)
  if (cityId === 'berlin' || cityId === 'cologne' || cityId === 'frankfurt' || cityId === 'tuebingen') {
    return cityId
  }

  return null
}

export function LocationAreaMap({
  className,
  fallbackMessage,
  mapCameraPadding,
  mapCities = [],
  mapControlLabels,
  mapLocations = [],
  mapView,
  selectedLocationId,
  universityRoute,
  viewCityLabel,
}: LocationAreaMapProps): React.ReactElement {
  const hoverCardIdPrefix = useId()
  const [mapError, setMapError] = useState(false)
  const [activePinId, setActivePinId] = useState<string | null>(null)
  const [preserveUserZoom, setPreserveUserZoom] = useState(false)
  const [longitude, latitude] = mapView.center
  const showOverviewMarkers = mapView.isOverview
  const activeCityId = mapView.isOverview ? null : mapView.id

  const handleUserNavigate = useCallback(() => {
    setPreserveUserZoom(true)
  }, [])

  const cityCardsBySlug = useMemo(
    () => new Map(mapCities.map((city) => [city.slug, city])),
    [mapCities],
  )
  const locationCardsBySlug = useMemo(
    () => new Map(mapLocations.map((location) => [location.slug, location])),
    [mapLocations],
  )

  const universityRouteKey = getUniversityRouteKey(universityRoute)

  useEffect(() => {
    setPreserveUserZoom(false)
    setActivePinId(null)
  }, [mapView.id, selectedLocationId, universityRouteKey])

  if (mapError) {
    return (
      <div
        className={cn(
          'flex size-full min-h-[16rem] items-center justify-center overflow-hidden rounded-2xl border border-dark-300/60 bg-white/70 text-center',
          className,
        )}
      >
        <p className="p-6 font-outfit text-sm text-dark-800">{fallbackMessage}</p>
      </div>
    )
  }

  return (
    <div className="size-full min-h-0">
      <MapCanvas
        attributionControl={{ compact: false }}
        boxZoom={false}
        center={[longitude, latitude]}
        className={cn('bg-cream', className)}
        cooperativeGestures
        doubleClickZoom
        dragPan
        dragRotate={false}
        interactive
        keyboard
        maxZoom={14}
        minZoom={4}
        scrollZoom={false}
        theme="light"
        touchPitch={false}
        touchZoomRotate
        zoom={mapView.zoom}
      >
      <LocationMapErrorWatcher onError={() => setMapError(true)} />
      <LocationMapStyle isOverview={mapView.isOverview} />
      <LocationMapNavigationHandlers onUserNavigate={handleUserNavigate} />
      <LocationMapCamera
        mapCameraPadding={mapCameraPadding}
        mapView={mapView}
        preserveUserZoom={preserveUserZoom}
        universityRoute={universityRoute}
      />
      <MapContentChrome
        maxZoom={14}
        minZoom={4}
        onZoomAdjust={handleUserNavigate}
        zoomGroupLabel={mapControlLabels.zoomGroupLabel}
        zoomInLabel={mapControlLabels.zoomInLabel}
        zoomOutLabel={mapControlLabels.zoomOutLabel}
      />
      {ALL_OVERVIEW_LOCATIONS.map((location) => {
        const cityId = getOverviewCityId(location.id)
        const cityCard = cityId ? cityCardsBySlug.get(cityId) : undefined
        if (!cityCard) {
          return null
        }

        return (
          <MapPinHoverCard
            cardId={`${hoverCardIdPrefix}-city-${cityId}`}
            cardType="city"
            cityCard={cityCard}
            isActive={activePinId === location.id}
            key={location.id}
            label={getOverviewMarkerName(mapView, location)}
            latitude={location.latitude}
            longitude={location.longitude}
            markerClassName={MARKER_PILL_CLASS}
            onActivate={() => setActivePinId(location.id)}
            onDeactivate={() =>
              setActivePinId((current) => (current === location.id ? null : current))
            }
            viewCityLabel={viewCityLabel}
            visible={showOverviewMarkers}
          />
        )
      })}
      {ALL_DETAIL_LOCATIONS.map((location) => {
        const locationCard = locationCardsBySlug.get(location.id)
        if (
          !locationCard ||
          location.city !== activeCityId ||
          !shouldShowDetailLocationPin(location.id, selectedLocationId)
        ) {
          return null
        }

        return (
          <MapPinHoverCard
            cardId={`${hoverCardIdPrefix}-location-${location.id}`}
            cardType="location"
            isActive={activePinId === location.id}
            key={location.id}
            label={location.name}
            latitude={locationCard.latitude}
            longitude={locationCard.longitude}
            locationCard={locationCard}
            markerClassName={cn(
              MARKER_PILL_CLASS,
              location.id === selectedLocationId && activePinId !== location.id
                ? 'ring-2 ring-turquoise/40'
                : undefined,
            )}
            onActivate={() => setActivePinId(location.id)}
            onDeactivate={() =>
              setActivePinId((current) => (current === location.id ? null : current))
            }
            visible={!showOverviewMarkers}
          />
        )
      })}
      {universityRoute ? <LocationAreaMapUniversityRoute {...universityRoute} /> : null}
      </MapCanvas>
    </div>
  )
}
